import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getForecast,
  getQuestion,
  getSavedMarketChecks,
  getSavedStatusChecks,
  getUserPredictions,
  marketCheckQuestion,
  saveMarketSnapshot,
  generateScenarios,
  saveScenarios,
  getSavedScenarios,
} from '../api/client'
import { useForecastStore } from '../store/forecastStore'
import { useSSE } from '../hooks/useSSE'
import ProbabilityChart from '../components/ProbabilityChart'
import AgentCard from '../components/AgentCard'
import SourcesList from '../components/SourcesList'
import ForecastDetailsPanel from '../components/ForecastDetailsPanel'
import RerunModal from '../components/RerunModal'
import ScheduledRerunsModal from '../components/ScheduledRerunsModal'
import AssignProjectModal from '../components/AssignProjectModal'
import NewProjectModal from '../components/NewProjectModal'
import MetaculusEmbed from '../components/MetaculusEmbed'
import ProgressSteps from '../components/ProgressSteps'
import FinalPredictionCard from '../components/FinalPredictionCard'
import SupervisorSummaryCard from '../components/SupervisorSummaryCard'
import UserPredictionModal from '../components/UserPredictionModal'
import ActionsMenu from '../components/ActionsMenu'
import Tooltip from '../components/Tooltip'
import ResolutionStatusChip from '../components/ResolutionStatusChip'
import SavedNewsSummaries from '../components/SavedNewsSummaries'
import SavedMarketSnapshots from '../components/SavedMarketSnapshots'
import SavedScenarios from '../components/SavedScenarios'
import ScenariosModal from '../components/ScenariosModal'
import type { ForecastQuestion, UserPrediction, ForecastRun, SavedStatusCheck, SavedScenarios as SavedScenariosType, GenerateScenariosResponse } from '../types'
import { useUIStore } from '../store/uiStore'
import { useSessionStore } from '../store/sessionStore'
import MarketCheckModal from '../components/MarketCheckModal'

export default function ResultsPage() {
  const { questionId } = useParams<{ questionId: string }>()
  const navigate = useNavigate()
  const {
    currentRuns,
    setCurrentRuns,
    setCurrentQuestionId,
    currentJobId,
    agentPredictions,
    activeAgentStates,
    predictionStageStarted,
    predictionStageComplete,
    supervisorStageStarted,
    supervisorStageComplete,
    finalStageComplete,
  } = useForecastStore()
  const [question, setQuestion] = useState<ForecastQuestion | null>(null)
  const [userPredictions, setUserPredictions] = useState<UserPrediction[]>([])
  const [savedNews, setSavedNews] = useState<SavedStatusCheck[]>([])
  const [savedMarkets, setSavedMarkets] = useState<SavedStatusCheck[]>([])
  const [savedScenariosList, setSavedScenariosList] = useState<SavedScenariosType[]>([])
  const [marketOpen, setMarketOpen] = useState(false)
  const [marketLoading, setMarketLoading] = useState(false)
  const [marketSaved, setMarketSaved] = useState(false)
  const [marketError, setMarketError] = useState<string | null>(null)
  const [marketData, setMarketData] = useState<any>(null)
  const [scenariosOpen, setScenariosOpen] = useState(false)
  const [scenariosLoading, setScenariosLoading] = useState(false)
  const [scenariosSaved, setScenariosSaved] = useState(false)
  const [scenariosError, setScenariosError] = useState<string | null>(null)
  const [scenariosData, setScenariosData] = useState<GenerateScenariosResponse | null>(null)
  const [activeRun, setActiveRun] = useState<ForecastRun | null>(null)
  const [resolutionCriteriaOpen, setResolutionCriteriaOpen] = useState(false)
  const initialisedRunIdRef = useRef<string | null>(null)
  const selectedRunId = useUIStore((s) => s.selectedRunId)
  const setSelectedRunId = useUIStore((s) => s.setSelectedRunId)
  const openModal = useUIStore((s) => s.openModal)
  const modals = useUIStore((s) => s.modals)
  const isAdmin = useSessionStore((s) => s.isAdmin)

  // Connect to SSE for live updates - prioritize currentJobId from store, then activeRun
  // This ensures we connect to the right job even before activeRun is set
  // Only connect for truly live runs (currentJobId is set when a run is running / created).
  const sseJobId = currentJobId
  useSSE(sseJobId)

  const loadSavedNews = async (id: string) => {
    try {
      const res = await getSavedStatusChecks(id, 10)
      setSavedNews(res.items || [])
    } catch {
      setSavedNews([])
    }
  }

  const loadSavedMarkets = async (id: string) => {
    try {
      const res = await getSavedMarketChecks(id, 10)
      setSavedMarkets(res.items || [])
    } catch {
      setSavedMarkets([])
    }
  }

  const loadSavedScenariosData = async (id: string) => {
    try {
      const res = await getSavedScenarios(id, 10)
      setSavedScenariosList(res.items || [])
    } catch {
      setSavedScenariosList([])
    }
  }

  useEffect(() => {
    if (questionId) {
      // Reset selection when navigating between questions
      setSelectedRunId(null)
      setCurrentQuestionId(questionId)
      loadQuestion(questionId)
      loadUserPredictions(questionId)
      loadSavedNews(questionId)
      loadSavedMarkets(questionId)
      loadSavedScenariosData(questionId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId])

  const runMarketCheck = async () => {
    if (!questionId) return
    setMarketLoading(true)
    setMarketError(null)
    setMarketSaved(false)
    try {
      const res = await marketCheckQuestion(questionId)
      setMarketData(res)
      setMarketOpen(true)
    } catch (e) {
      setMarketError(e instanceof Error ? e.message : 'Failed to search prediction markets')
      setMarketOpen(true)
    } finally {
      setMarketLoading(false)
    }
  }

  const saveMarket = async () => {
    if (!questionId || !marketData) return
    try {
      setMarketSaved(false)
      await saveMarketSnapshot(questionId, marketData)
      setMarketSaved(true)
      await loadSavedMarkets(questionId)
    } catch (e) {
      setMarketError(e instanceof Error ? e.message : 'Failed to save market snapshot')
    }
  }

  const runGenerateScenarios = async () => {
    if (!questionId) return
    setScenariosLoading(true)
    setScenariosError(null)
    setScenariosSaved(false)
    try {
      const res = await generateScenarios(questionId)
      setScenariosData(res)
      setScenariosOpen(true)
    } catch (e) {
      setScenariosError(e instanceof Error ? e.message : 'Failed to generate scenarios')
      setScenariosOpen(true)
    } finally {
      setScenariosLoading(false)
    }
  }

  const saveGeneratedScenarios = async () => {
    if (!questionId || !scenariosData) return
    try {
      setScenariosSaved(false)
      await saveScenarios(questionId, scenariosData.scenarios, scenariosData.probability)
      setScenariosSaved(true)
      await loadSavedScenariosData(questionId)
    } catch (e) {
      setScenariosError(e instanceof Error ? e.message : 'Failed to save scenarios')
    }
  }

  // Set active run (most recent running or completed) and initialize agent states
  useEffect(() => {
    if (currentRuns.length > 0) {
      // Find running run first, otherwise use most recent
      const run =
        (selectedRunId && currentRuns.find((r) => r.job_id === selectedRunId)) ||
        currentRuns.find((r) => r.status === 'running') ||
        currentRuns[0]
      setActiveRun(run)

      // IMPORTANT: only initialise agent states once per run.
      // Otherwise when `currentRuns` updates (e.g. forecast completes) we overwrite "complete" back to "pending".
      if (initialisedRunIdRef.current !== run.job_id) {
        initialisedRunIdRef.current = run.job_id

        const numAgents = run.config?.num_agents || 6
        const store = useForecastStore.getState()
        for (let i = 1; i <= numAgents; i++) {
          if (!store.activeAgentStates[i]) {
            store.updateAgentState(i, {
              status: 'pending',
              reasoning: '',
              searches: [],
            })
          }
        }
      }
    }
  }, [currentRuns, selectedRunId])

  const loadQuestion = async (id: string) => {
    try {
      const q = await getQuestion(id)
      setQuestion(q)
      if (q.runs) {
        setCurrentRuns(q.runs)
        // Only set currentJobId automatically if the most recent run is actually running.
        // This prevents SSE connecting for historical/completed questions.
        if (q.runs.length > 0 && !currentJobId && q.runs[0].status === 'running') {
          const mostRecentRun = q.runs[0]
          if (mostRecentRun.job_id) {
            useForecastStore.getState().setCurrentJobId(mostRecentRun.job_id)
          }
        }
      }
    } catch (error) {
      console.error('Failed to load question:', error)
      navigate('/')
    }
  }

  useEffect(() => {
    // If the active run is complete but missing agent predictions (older runs), fetch the full run record.
    if (!activeRun || activeRun.status !== 'complete') return

    const stored = (activeRun.agent_predictions || []).length
    const stages = (activeRun as any).stages
    const derived = Array.isArray(stages?.prediction_agents) ? stages.prediction_agents.length : 0

    if (stored > 0 || derived > 0) return

    void (async () => {
      try {
        const refreshed = await getForecast(activeRun.job_id)
        setCurrentRuns(
          useForecastStore
            .getState()
            .currentRuns.map((r) => (r.job_id === refreshed.job_id ? refreshed : r))
        )
        setActiveRun(refreshed)
      } catch {
        // Non-fatal: we’ll show what we can.
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.job_id])

  const loadUserPredictions = async (id: string) => {
    try {
      const predictions = await getUserPredictions(id)
      setUserPredictions(predictions)
    } catch (error) {
      console.error('Failed to load user predictions:', error)
    }
  }

  const handleUserPredictionSave = () => {
    // Reload user predictions and update chart
    if (questionId) {
      loadUserPredictions(questionId)
    }
  }

  const handleSelectRun = async (jobId: string) => {
    setSelectedRunId(jobId)
    const found = currentRuns.find((r) => r.job_id === jobId)
    if (found) setActiveRun(found)

    // If the run is complete, refresh it to ensure we have final_result/token_summary
    if (found?.status === 'complete') {
      try {
        const refreshed = await getForecast(jobId)
        setCurrentRuns(
          useForecastStore
            .getState()
            .currentRuns.map((r) => (r.job_id === refreshed.job_id ? refreshed : r))
        )
        setActiveRun(refreshed)
      } catch (e) {
        // Non-fatal: we can still display what we already have.
      }
    }
  }

  const handleDownloadJSON = async () => {
    if (!activeRun) return
    try {
      const run = await getForecast(activeRun.job_id)
      const payload = JSON.stringify(run, null, 2)
      const blob = new Blob([payload], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      const safeStem = ((question?.formatted_question as string) || 'forecast')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 48)
      const idSnippet = run.job_id.slice(-8)
      const date = new Date(run.created_at).toISOString().slice(0, 10)
      const filename = `forecast_${safeStem}_${idSnippet}_${date}.json`

      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Failed to download JSON:', e)
      alert(`Failed to download JSON: ${e instanceof Error ? e.message : 'Unknown error'}`)
    }
  }

  // Get agent configs from active run
  const getAgentConfigs = () => {
    if (!activeRun?.config?.agent_configs) return []
    return activeRun.config.agent_configs
  }

  const isViewingLiveRun = Boolean(activeRun && currentJobId && activeRun.job_id === currentJobId)

  const urlHostname = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch {
      return url
    }
  }

  const deriveAgentPredictionsFromStages = (run: ForecastRun): any[] => {
    const stages = (run as any).stages
    const predictionStages: any[] = Array.isArray(stages?.prediction_agents) ? stages.prediction_agents : []
    if (predictionStages.length === 0) return []

    return predictionStages
      .filter((s) => s && typeof s.agent_id === 'number' && s.output)
      .map((s) => {
        const out = s.output || {}
        return {
          agent_id: s.agent_id,
          probability:
            typeof out.raw_probability === 'number'
              ? out.raw_probability
              : typeof out.probability === 'number'
                ? out.probability
                : 0,
          confidence: out.qualitative_confidence || out.confidence || 'medium',
          reasoning: out.reasoning || '',
          question_restatement: out.question_restatement,
          key_evidence: out.key_evidence || [],
          notes: out.notes,
          visited_urls: s.visited_urls || [],
          searches: s.searches || [],
          reasoning_trace: s.reasoning_summary || '',
        }
      })
  }

  const getStoredAgentPredictionsForRun = (run: ForecastRun): any[] => {
    const stored = run.agent_predictions || []
    if (stored.length > 0) return stored
    return deriveAgentPredictionsFromStages(run)
  }

  const getDisplayedAgentPrediction = (agentId: number) => {
    if (!activeRun) return undefined
    if (isViewingLiveRun) {
      return agentPredictions.find((p) => p.agent_id === agentId)
    }
    return getStoredAgentPredictionsForRun(activeRun).find((p) => p.agent_id === agentId)
  }

  // Get number of agents
  const numAgents = activeRun?.config?.num_agents || 6

  // Get sources from active run
  const getSources = (): any[] => {
    if (activeRun?.final_result?.visited_sources) {
      return activeRun.final_result.visited_sources
    }
    // If we are viewing a completed/historic run, do NOT fall back to live agent state.
    // Instead, build a minimal sources list from agent_predictions.visited_urls.
    if (activeRun && !isViewingLiveRun) {
      const urls = new Map<string, { agent_count: number }>()
      for (const ap of activeRun.agent_predictions || []) {
        for (const u of ap.visited_urls || []) {
          const url = typeof u === 'string' ? u : u?.url
          if (!url) continue
          const prev = urls.get(url)
          urls.set(url, { agent_count: (prev?.agent_count || 0) + 1 })
        }
      }
      return Array.from(urls.entries()).map(([url, meta]) => ({
        url,
        title: urlHostname(url),
        agent_count: meta.agent_count,
      }))
    }
    // Fallback: aggregate from agent states
    const sourcesMap = new Map<string, { title: string; agent_count: number }>()
    Object.values(activeAgentStates).forEach((state) => {
      state.searches.forEach((search) => {
        search.results?.forEach((result: any) => {
          if (result.url) {
            const existing = sourcesMap.get(result.url)
            if (existing) {
              existing.agent_count++
            } else {
              sourcesMap.set(result.url, {
                title: result.title || result.url,
                agent_count: 1,
              })
            }
          }
        })
      })
    })
    return Array.from(sourcesMap.entries()).map(([url, data]) => ({
      url,
      title: data.title,
      agent_count: data.agent_count,
    }))
  }

  if (!question) {
    return (
      <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs text-muted">Loading…</p>
        </div>
      </div>
    )
  }

  const isFinalForSelectedRun = Boolean(activeRun && (finalStageComplete || activeRun.status === 'complete'))
  const phase: 'agents' | 'supervisor' | 'final' = isFinalForSelectedRun
    ? 'final'
    : supervisorStageStarted
      ? 'supervisor'
      : 'agents'

  const AgentsAndSources = activeRun ? (
    <>
      <section className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
        <h2 className="font-semibold text-lg sm:text-xl text-ink">
          {activeRun.status === 'running' && isViewingLiveRun
            ? `Agent work (${numAgents})`
            : `Agent predictions (${numAgents})`}
        </h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {Array.from({ length: numAgents }, (_, i) => {
            const agentId = i + 1
            const agentConfig = getAgentConfigs()[i]
            const prediction = getDisplayedAgentPrediction(agentId)
            return (
              <AgentCard
                key={agentId}
                agentId={agentId}
                agentName={agentConfig?.name}
                prediction={prediction}
                useLiveState={activeRun.status === 'running' && isViewingLiveRun}
              />
            )
          })}
        </div>
      </section>

      <SourcesList sources={getSources()} />
    </>
  ) : null

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 sm:gap-6 items-start">
          <div className="min-w-0">
            <h1 className="font-serif text-xl sm:text-2xl text-ink font-semibold leading-snug">
              {question.formatted_question}
            </h1>
            {question.resolution_criteria && (
              <div className="mt-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <ResolutionStatusChip question={question} variant="label" onSaved={() => questionId && loadSavedNews(questionId)} />
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-xs text-muted hover:text-ink transition"
                    onClick={() => setResolutionCriteriaOpen((v) => !v)}
                    aria-expanded={resolutionCriteriaOpen}
                  >
                    <span>Resolution criteria</span>
                    <span className="text-[10px]">{resolutionCriteriaOpen ? '▾' : '▸'}</span>
                  </button>
                </div>
                {resolutionCriteriaOpen && (
                  <p className="mt-2 text-sm text-muted leading-relaxed">{question.resolution_criteria}</p>
                )}
              </div>
            )}
          </div>
          {activeRun?.final_result && (
            <div className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 text-center min-w-[180px]">
              <div className="font-serif text-4xl leading-none">
                {typeof activeRun.final_result.probability === 'number'
                  ? `${Math.round(activeRun.final_result.probability * 100)}%`
                  : '--'}
              </div>
              <div className="mt-2 text-xs text-muted tracking-wider font-semibold">
                {(activeRun.final_result.confidence || '--').toString().toUpperCase()} CONFIDENCE
              </div>
            </div>
          )}
        </div>

        {activeRun?.status !== 'complete' && !finalStageComplete && (
          <ProgressSteps
            predictionStarted={predictionStageStarted}
            predictionDone={predictionStageComplete}
            supervisorStarted={supervisorStageStarted}
            supervisorDone={supervisorStageComplete}
            finalDone={finalStageComplete}
          />
        )}

        <MetaculusEmbed question={question} />

        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Tooltip content="Add your own prediction and compare it to the AI's forecasts.">
              <button
                className="rounded-xl px-3 py-2 text-sm font-semibold bg-green text-white shadow-delphi hover:brightness-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                type="button"
                onClick={() => openModal('userPrediction')}
                disabled={!questionId}
                title={!questionId ? 'Open a forecast first.' : ''}
              >
                Add your own prediction
              </button>
            </Tooltip>
            <ResolutionStatusChip question={question} variant="button" onSaved={() => questionId && loadSavedNews(questionId)} />
            <Tooltip content="Find closely related prediction markets on Metaculus, Polymarket, OddsChecker and Kalshi. This can take 2–3 mins to run.">
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm font-semibold bg-green text-white shadow-delphi hover:brightness-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => void runMarketCheck()}
                disabled={!questionId || marketLoading}
              >
                {marketLoading ? 'Searching prediction markets…' : 'Find related prediction markets'}
              </button>
            </Tooltip>

            {isAdmin && (
              <Tooltip content="Generate illustrative scenarios showing how the forecast outcome might (or might not) unfold. Uses GPT-5 Mini with medium reasoning.">
                <button
                  type="button"
                  className="rounded-xl px-3 py-2 text-sm font-semibold bg-green text-white shadow-delphi hover:brightness-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => void runGenerateScenarios()}
                  disabled={!questionId || scenariosLoading || !activeRun?.final_result}
                  title={!activeRun?.final_result ? 'Complete a forecast first.' : ''}
                >
                  {scenariosLoading ? 'Generating scenarios…' : 'Generate scenarios'}
                </button>
              </Tooltip>
            )}

            <Tooltip content="Run the forecasting pipeline again for this question.">
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm font-semibold bg-green text-white shadow-delphi hover:brightness-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => openModal('rerun')}
                disabled={!questionId || currentRuns.length === 0}
                title={!questionId ? 'Open a forecast first.' : ''}
              >
                Rerun forecast
              </button>
            </Tooltip>
          </div>
          <ActionsMenu
            onRerun={() => openModal('rerun')}
            onDownload={handleDownloadJSON}
            onAddToProject={() => openModal('assignProject')}
            disabled={!questionId || currentRuns.length === 0}
          />
        </div>

        {phase === 'final' && activeRun?.final_result && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-4 sm:gap-6 items-start">
            <section className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
              {(() => {
                const completedRuns = currentRuns
                  .filter((r) => r.final_result)
                  .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                if (completedRuns.length > 1) {
                  return (
                    <div className="flex flex-wrap gap-2">
                      {completedRuns.map((run, index) => {
                        const isActive = activeRun?.job_id === run.job_id
                        return (
                          <button
                            key={run.job_id}
                            type="button"
                            className={[
                              'rounded-delphi',
                              'border',
                              isActive ? 'border-green/40 bg-green/10' : 'border-rule bg-paper',
                              'shadow-delphi',
                              'px-3',
                              'py-2.5',
                              'min-w-[120px]',
                              'text-center',
                              'hover:bg-white/30',
                              'transition',
                            ].join(' ')}
                            onClick={() => handleSelectRun(run.job_id)}
                          >
                            <div className="text-[11px] text-muted">Prediction {index + 1}</div>
                            <div className="font-serif text-2xl leading-tight text-ink">
                              {((run.final_result?.probability || 0) * 100).toFixed(1)}%
                            </div>
                            <div className="text-[10px] text-muted tracking-wider font-semibold">
                              {(run.final_result?.confidence || 'MEDIUM').toUpperCase()}
                            </div>
                            <div className="font-mono text-[10px] text-muted mt-1">
                              {new Date(run.created_at).toLocaleDateString()}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )
                }
                return <FinalPredictionCard run={activeRun} embedded />
              })()}

              <div className="mt-4 border-t border-rule pt-4">
                <SupervisorSummaryCard finalResult={activeRun.final_result} embedded />
              </div>
            </section>
            <div className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-3 sm:p-4">
              <ProbabilityChart runs={currentRuns} userPredictions={userPredictions} height={240} />
              <SavedNewsSummaries items={savedNews} />
              <SavedMarketSnapshots items={savedMarkets} />
              {isAdmin && <SavedScenarios items={savedScenariosList} />}
            </div>
          </div>
        )}

        <MarketCheckModal
          open={marketOpen}
          onClose={() => setMarketOpen(false)}
          data={marketData}
          error={marketError}
          onSave={() => void saveMarket()}
          saving={marketLoading}
          saved={marketSaved}
        />

        <ScenariosModal
          open={scenariosOpen}
          onClose={() => setScenariosOpen(false)}
          data={scenariosData}
          error={scenariosError}
          loading={scenariosLoading}
          onSave={() => void saveGeneratedScenarios()}
          saving={false}
          saved={scenariosSaved}
        />

        {phase !== 'agents' && (
          <section className="mt-6 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
            <ForecastDetailsPanel run={activeRun} />
          </section>
        )}

        {phase === 'agents' && AgentsAndSources}
        {phase !== 'agents' && <div className="mt-6">{AgentsAndSources}</div>}

        <section className="mt-10 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
          <h2 className="font-semibold text-lg sm:text-xl text-ink">
            Forecast runs ({currentRuns.length})
          </h2>
          {currentRuns.length === 0 ? (
            <p className="mt-2 text-sm text-muted">No runs yet. Start a forecast to see results here.</p>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentRuns.map((run) => (
                <button
                  key={run.job_id}
                  className={[
                    'text-left',
                    'rounded-delphi',
                    'border',
                    activeRun?.job_id === run.job_id ? 'border-green/40 bg-green/10' : 'border-rule bg-paper',
                    'shadow-delphi',
                    'px-4',
                    'py-3',
                    'hover:bg-white/30',
                    'transition',
                  ].join(' ')}
                  type="button"
                  onClick={() => handleSelectRun(run.job_id)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-xs text-muted">{run.job_id.slice(0, 8)}…</span>
                    <span className="text-[10px] tracking-wider text-muted font-semibold">{run.status.toUpperCase()}</span>
                  </div>
                  {run.final_result && (
                    <div className="mt-2 flex items-baseline justify-between gap-3">
                      <div className="font-serif text-2xl text-ink">
                        {(run.final_result.probability * 100).toFixed(1)}%
                      </div>
                      <div className="text-[10px] text-muted tracking-wider font-semibold">
                        {(run.final_result.confidence || 'MEDIUM').toString().toUpperCase()}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 text-[11px] text-muted">
                    Agents: {run.config?.num_agents ?? '--'} • Models:{' '}
                    {run.config?.models?.predictor || run.config?.predictor_model || '--'} /{' '}
                    {run.config?.models?.supervisor || run.config?.supervisor_model || '--'}
                    {run.total_duration_ms != null && (
                      <> • Duration: {Math.round(run.total_duration_ms / 1000)}s</>
                    )}
                    {run.token_summary?.total_in != null && run.token_summary?.total_out != null && (
                      <> • Tokens: {run.token_summary.total_in.toLocaleString()}/{run.token_summary.total_out.toLocaleString()}</>
                    )}
                    {run.token_summary?.estimated_cost_usd != null && <> • Cost: ${run.token_summary.estimated_cost_usd.toFixed(6)}</>}
                  </div>
                  <div className="mt-2 font-mono text-[11px] text-muted">
                    Created: {new Date(run.created_at).toLocaleString()}
                    {run.completed_at && (
                      <> • Completed: {new Date(run.completed_at).toLocaleString()}</>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {modals.rerun && <RerunModal questionId={questionId} activeRun={activeRun} />}
        {modals.scheduleReruns && <ScheduledRerunsModal questionId={questionId} activeRun={activeRun} />}
        {modals.assignProject && <AssignProjectModal questionId={questionId} />}
        {modals.newProject && <NewProjectModal />}
        {modals.userPrediction && (
          <UserPredictionModal questionId={questionId} onSaved={handleUserPredictionSave} />
        )}
      </div>
    </div>
  )
}
